import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserContext";
import { useScrollLock } from "../hooks/useScrollLock";
import ErrorModal from "./ErrorModal";
import { useApi } from "../utils/Api";
import ItemModal from "./ItemModal";

// ✅ Spinner Overlay Component
const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

const Basket = ({ onCheckoutClick, isMobileCheckout }) => {
  const { apiFetch } = useApi();
  const { cartItems, updateCartItemQuantity, removeItemFromCart } = useCart();
  const { user, openLoginModal } = useUser();
  const [isMobileBasketOpen, setIsMobileBasketOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutType, setCheckoutType] = useState("eat-in");
  const [editingCartItem, setEditingCartItem] = useState(null);

  useScrollLock(isMobileBasketOpen);

  const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  // ✅ SINGLE SOURCE OF TRUTH FOR TOTALS
  const getItemTotals = (cartItem) => {
    if (!cartItem || !cartItem.item) return { total: 0, options: [] };

    let total = Number(cartItem.item.price || 0);
    const options = [];

    if (cartItem.selectedRequiredOption) {
      total += Number(cartItem.selectedRequiredOption.price || 0);
      options.push({
        name: cartItem.selectedRequiredOption.name,
        price: Number(cartItem.selectedRequiredOption.price || 0).toFixed(2),
      });
    }

    [...(cartItem.item.swallowOptions || []), ...(cartItem.item.proteins || [])]
      .forEach((opt) => {
        const optTotal = Number(opt.option_price || 0) * (Number(opt.quantity) || 1);
        total += optTotal;
        options.push({
          name: opt.option_name + (opt.quantity > 1 ? ` x${opt.quantity}` : ""),
          price: optTotal.toFixed(2),
        });
      });

    return { total, options };
  };

  // ✅ GRAND TOTAL
  const grandTotal = cartItems
    .reduce((acc, cartItem) => {
      const { total } = getItemTotals(cartItem);
      return acc + total * cartItem.quantity;
    }, 0)
    .toFixed(2);

  // 🟢 FAIL-OPEN STOCK CHECK
  const checkStockBeforeCheckout = async () => {
    try {
      const data = await apiFetch("check-stock", { cartItems });

      if (!data || data.success !== true) {
        console.warn("Stock check unavailable — allowing checkout");
        return true;
      }

      if (data.soldOutItems?.length > 0) {
        const names = data.soldOutItems.map((item) => item.item_name).join(", ");
        setErrorMessage(
          <>
            The following items are sold out: <b>{names}</b>. <br />
            Please remove them from your basket to proceed.
          </>
        );
        return false;
      }

      return true;
    } catch (err) {
      console.warn("Stock check error — allowing checkout:", err);
      return true;
    }
  };

  // 🔐 SINGLE CHECKOUT GUARD
  const BLOCKED_DELIVERY_IDS = [9, 11, 12, 16];

  const handleCheckout = async (type) => {
    if (!user?.id) {
      openLoginModal();
      return;
    }

    if (isCheckingOut) return;
    setIsCheckingOut(true);

    // Delivery-only rules
    if (type === "delivery") {
      const hasBlockedItem = cartItems.some((c) =>
        BLOCKED_DELIVERY_IDS.includes(Number(c.item.category_id))
      );
      if (hasBlockedItem) {
        setErrorMessage(
          "Alcoholic drinks cannot be delivered. Please remove them to proceed with delivery."
        );
        setIsCheckingOut(false);
        return;
      }
    }

    const canProceed = await checkStockBeforeCheckout();
    if (!canProceed) {
      setIsCheckingOut(false);
      return;
    }

    await onCheckoutClick(type);
    setIsCheckingOut(false);
  };

  const renderCartItems = () =>
    cartItems.map((cartItem) => {
      const { item, quantity, key } = cartItem;
      const { total, options } = getItemTotals(cartItem);
      const finalTotal = (total * quantity).toFixed(2);

      return (
        <li key={key} className="cart-item">
          <div className="cart-item-left">
            <div className="cart-item-header">
              <span className="cart-item-name">{item.item_name}</span>
              <span className="cart-item-price">£{Number(item.price || 0).toFixed(2)}</span>
            </div>

            {options.map((opt, idx) => (
              <div key={idx} className="cart-item-option">
                <span className="option-name">{opt.name}</span>
                <span className="option-price">£{opt.price}</span>
              </div>
            ))}

            <div className="cart-item-footer">
              <span className="final-total">£{finalTotal}</span>
              <div className="cart-controls">
                <button
                  className="qty-btn"
                  onClick={() =>
                    quantity > 1
                      ? updateCartItemQuantity(key, quantity - 1)
                      : removeItemFromCart(key)
                  }
                  disabled={isCheckingOut}
                >
                  −
                </button>
                <span>{quantity}</span>
                <button
                  className="qty-btn"
                  onClick={() => updateCartItemQuantity(key, quantity + 1)}
                  disabled={isCheckingOut}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="cart-item-right">
            <button
              className="edit-btn"
              onClick={() => setEditingCartItem(cartItem)}
              disabled={isCheckingOut}
            >
              <i className="fa fa-pen"></i>
            </button>

            <button
              className="delete-btn"
              onClick={() => removeItemFromCart(key)}
              disabled={isCheckingOut}
            >
              <i className="fa fa-trash"></i>
            </button>
          </div>
        </li>
      );
    });

  return (
    <>
      {/* ✅ Show Spinner Overlay while checking out */}
      {isCheckingOut && <SpinnerOverlay />}

      {/* DESKTOP BASKET */}
      {!isMobileCheckout && (
        <aside className="basket-section desktop-basket">
          <div className="basket-header">
            <h3 className="basket-title">
              <span className="cart-icon">
                <i className="fa fa-shopping-cart"></i>
                {cartItems.length > 0 && <span className="cart-count">{cartCount}</span>}
              </span>
              Basket
            </h3>
          </div>

          {cartItems.length === 0 ? (
            <p className="empty-basket">Your basket is empty</p>
          ) : (
            <ul className="basket-list">{renderCartItems()}</ul>
          )}

          {cartItems.length > 0 && (
            <div className="checkout-box-container">
              <div className="checkout-box">
                <div className="checkout-toggle-container">
                  <div className="checkout-toggle">
                    <button
                      className={checkoutType === "eat-in" ? "active" : ""}
                      onClick={() => setCheckoutType("eat-in")}
                      disabled={isCheckingOut}
                    >
                      <i className="fa-solid fa-utensils"></i> Eat-in
                    </button>

                    <button
                      className={checkoutType === "pickup" ? "active" : ""}
                      onClick={() => setCheckoutType("pickup")}
                      disabled={isCheckingOut}
                    >
                      <i className="fa-solid fa-bag-shopping"></i> Pick-up
                    </button>
                  </div>
                </div>

                <div className="checkout-action">
                  <button
                    className="order-btn flex-space"
                    onClick={() => handleCheckout(checkoutType)}
                    disabled={isCheckingOut}
                  >
                    <span>Checkout</span>
                    <span className="order-price">£{grandTotal}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </aside>
      )}

      {/* MOBILE BOTTOM BAR */}
      {isMobileCheckout && cartItems.length > 0 && (
        <>
          <div className="mobile-checkout-bar">
            <div className="mobile-checkout-bar-left">
              <span
                className="cart-icon"
                onClick={() => setIsMobileBasketOpen(!isMobileBasketOpen)}
                style={{ cursor: "pointer" }}
              >
                <i className="fa fa-shopping-cart"></i>
                <span className="cart-count">{cartCount}</span>
              </span>
            </div>

            <div className="mobile-checkout-bar-right">
              <button
                className="add-to-cart-button mobile-checkout-btn"
                onClick={() => handleCheckout(null)}
                disabled={isCheckingOut}
              >
                Checkout &nbsp; £{grandTotal}
              </button>
            </div>
          </div>

          {isMobileBasketOpen && (
            <div className="mobile-basket-container open">
              <ul className="basket-list">{renderCartItems()}</ul>
            </div>
          )}
        </>
      )}

      {errorMessage && (
        <ErrorModal message={errorMessage} onClose={() => setErrorMessage("")} />
      )}

      {editingCartItem && (
        <ItemModal
          isOpen={true}
          item={editingCartItem.item}
          cartItem={editingCartItem}
          onClose={() => setEditingCartItem(null)}
          mode={editingCartItem ? "edit" : "add"}
        />
      )}
    </>
  );
};

export default Basket;
